import { setItemValue } from '@/factories/lessonUtils';
import parseVideoUrl from '@/lib/videoPlayer/parseVideoUrl';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';

const tag = (value) => {
  return (
    value
      .trim()
      .toLowerCase()
      //remove all non word chars with _
      .replace(/[^\w\_]/g, '_')
      //replace all __ with _
      .replace(/_{2,}/g, '_')
      //remove leading or trailing _
      .replace(/(^_|_$)/g, '')
      .replace(/^(\d+)$/, 'id_$1')
  );
};

const filters = {
  tag,

  video(value) {
    return parseVideoUrl(value, 'clean');
  },

  tagify(list = [], schema) {
    return list.map((object) => {
      schema.tagify.forEach((key) => {
        object[key] = tag(object[key]);
      });
      return object;
    });
  },
};

export default {
  // individual field schema
  // the field key being updated
  // and the uuid of the lesson type object
  props: [
    'item',
    'schema',
    'field',
    'value',
    'meta',
    'rules',
    'disabled',
    'lessonId',
    'errorMessages',
    'successMessages',
  ],
  methods: {
    filters(value) {
      if (this.schema && isArray(this.schema.filters)) {
        this.schema.filters.forEach((filter) => {
          if (filters[filter]) {
            value = filters[filter](value, this.schema);
          }
        });
      }
      return value;
    },

    save(value) {
      value = this.filters(value);

      let vals = cloneDeep(this.item.values);
      // Complete reset so change detection works
      this.item.values = setItemValue(vals, this.field, value);
    },

    truncate(property, length) {
      let p = '' + (property || '');

      if (p.length < length) {
        return p;
      }

      return p.slice(0, length) + '...';
    },

    checkHavingConditions(having) {
      if (!having) return null;

      const targetValues =
        this.item?.values ??
        this.schema?.model ??
        this.meta?.parentInstance ??
        null;

      if (!targetValues) return null;

      // Simple string condition: field must exist & be truthy
      if (typeof having === 'string') {
        return Boolean(targetValues[having]);
      }

      if (typeof having !== 'object') {
        return null;
      }

      const useParent =
        this.schema?.toggleHavingTargetsParent && this.meta?.parentInstance;

      const source = useParent ? this.meta.parentInstance : targetValues;

      for (const group of Object.values(having)) {
        if (typeof group !== 'object') continue;

        for (const [fieldKey, expectedValue] of Object.entries(group)) {
          const actualValue = source[fieldKey];

          if (Array.isArray(expectedValue)) {
            if (!expectedValue.includes(actualValue)) {
              return false;
            }
          } else {
            if (actualValue !== expectedValue) {
              return false;
            }
          }
        }
      }

      return true;
    },
  },
  computed: {
    showTitle() {
      const { hide_header, title } = this.schema || {};
      return !hide_header && title;
    },

    shouldBeShown() {
      const result = this.checkHavingConditions(this.schema.showOnHaving);
      return result === null ? true : result;
    },

    shouldBeHidden() {
      const result = this.checkHavingConditions(this.schema.hideOnHaving);
      return result === null ? false : result;
    },

    isVisible() {
      if (!this.schema) {
        return true;
      } // If schema isn't defined, always show
      return !this.schema.hidden && this.shouldBeShown && !this.shouldBeHidden;
    },

    localValue: {
      get() {
        return this.value;
      },
      set(value) {
        if (this.item) {
          this.save(value);
        } else {
          this.$emit('input', value);
        }
      },
    },

    fieldClasses() {
      return {
        'background-dark': this.schema?.dark,
        'no-label': this.schema?.hideLabel,
      };
    },

    isDisabled() {
      const { disabled, checkDisabled } = this.schema;
      if (typeof checkDisabled === 'function' && this.item) {
        return checkDisabled(this.item);
      }
      return this.disabled || disabled;
    },

    getAlertMessage() {
      const { checkAlert } = this.schema;
      if (typeof checkAlert === 'function' && this.item) {
        return checkAlert(this.item);
      }
      return null;
    },
  },
};
